

import { MetricIds } from 'src/core/models/enum/metric-ids.enum';
import { API_PREFIX } from 'src/core/constants/api.constants';
import { DashboardCardItem } from '../interfaces/dashboard-card-item.interface';
import { DashboardWidgetDefinition } from '../interfaces/dashboard-widget-definition.interface';
import { MetricEndpoints } from '../interfaces/metric-endpoints.interface';
import { WidgetDefinitionFromConstants } from '../interfaces/widget-definition-from-constants.interface';
import { DashboardHelper } from '../helpers/dashboard.helper';

export type { DashboardCardItem, DashboardWidgetDefinition, MetricEndpoints, WidgetDefinitionFromConstants };

const API_BASE = `${API_PREFIX}/dashboard`;

export const GROUPED_METRIC_IDS: readonly string[] = ['csat', 'estimated-csat', 'negative-sentiment', 'nps'];

export const DASHBOARD_WIDGET_DEFS: DashboardWidgetDefinition[] = [
  {
    id: 'ai-resolved',
    component: 'ai-resolved',
    titleKey: 'Dashboard::AiResolvedRate',
    descriptionKey: 'Dashboard::AiResolvedDescription',
    metricDescriptionKey: 'Dashboard::AiResolvedDescription',
    icon: 'fa-chart-pie',
    isLarge: true,
    layout: 'main',
    metricId: MetricIds.AiResolvedConversation,
    previewValue: { value: 280, totalCount: 1000 },
    endpoints: {
      value: `${API_BASE}/ai-resolved-rate-value`,
      trend: `${API_BASE}/ai-resolved-rate-trend`,
    },
  },
  {
    id: 'grouped-metrics',
    component: 'grouped-metrics',
    titleKey: 'Dashboard::CustomerSatisfaction',
    descriptionKey: 'Dashboard::CustomerSatisfactionDescription',
    icon: 'fa-chart-line',
    isLarge: true,
    layout: 'main',
    desktopOnly: true,
  },
  {
    id: 'average-handle-time',
    component: 'average-handle-time',
    titleKey: 'Dashboard::AverageHandleTime',
    descriptionKey: 'Dashboard::WidgetAhtDesc',
    metricDescriptionKey: 'Dashboard::AverageHandleTimeDescription',
    icon: 'fa-clock',
    layout: 'main',
    metricId: MetricIds.Aht,
    previewValue: { value: 3600, totalCount: 60 },
    endpoints: {
      value: `${API_BASE}/average-handle-time-value`,
      trend: `${API_BASE}/average-handle-time-trend`,
    },
  },
  {
    id: 'nonfcr',
    component: 'nonfcr',
    titleKey: 'Dashboard::NonFcr',
    descriptionKey: 'Dashboard::WidgetNonfcrDesc',
    metricDescriptionKey: 'Dashboard::NonfcrDescription',
    icon: 'fa-chart-bar',
    layout: 'main',
    metricId: MetricIds.Nonfcr,
    previewValue: { value: 72, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/nonfcr-value`,
      trend: `${API_BASE}/nonfcr-trend`,
    },
  },
  {
    id: 'transfer-rate',
    component: 'transfer-rate',
    titleKey: 'Dashboard::TransferRate',
    descriptionKey: 'Dashboard::WidgetTransferRateDesc',
    metricDescriptionKey: 'Dashboard::TransferRateDescription',
    icon: 'fa-arrows-rotate',
    layout: 'main',
    metricId: MetricIds.TransferRate,
    previewValue: { value: 32, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/transfer-rate-value`,
      trend: `${API_BASE}/transfer-rate-trend`,
    },
  },
  {
    id: 'handover-rate',
    component: 'handover-rate',
    titleKey: 'Dashboard::HandoverRate',
    descriptionKey: 'Dashboard::WidgetHandoverRateDesc',
    metricDescriptionKey: 'Dashboard::HandoverRateDescription',
    icon: 'fa-right-left',
    layout: 'main',
    metricId: MetricIds.HandoverRate,
    previewValue: { value: 18, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/handover-rate-value`,
      trend: `${API_BASE}/handover-rate-trend`,
    },
  },
  {
    id: 'wrap-up-codes',
    component: 'wrap-up-codes',
    titleKey: 'Dashboard::WrapUpCodes',
    descriptionKey: 'Dashboard::WrapUpCodesDescription',
    icon: 'fa-chart-pie',
    layout: 'main',
  },
  {
    id: 'csat',
    component: 'csat',
    titleKey: 'Dashboard::CSAT',
    descriptionKey: 'Dashboard::WidgetCsatDesc',
    metricDescriptionKey: 'Dashboard::CsatDescription',
    icon: 'fa-face-smile',
    layout: 'grouped',
    metricId: MetricIds.Csat,
    previewValue: { value: 480, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/csat-value`,
      trend: `${API_BASE}/csat-trend`,
    },
  },
  {
    id: 'estimated-csat',
    component: 'estimated-csat',
    titleKey: 'Dashboard::EstimatedCsat',
    descriptionKey: 'Dashboard::WidgetEstimatedCsatDesc',
    metricDescriptionKey: 'Dashboard::EstimatedCsatDescription',
    icon: 'fa-face-smile',
    layout: 'grouped',
    metricId: MetricIds.EstimatedCsat,
    previewValue: { value: 455, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/estimated-csat-value`,
      trend: `${API_BASE}/estimated-csat-trend`,
    },
  },
  {
    id: 'negative-sentiment',
    component: 'negative-sentiment',
    titleKey: 'Dashboard::NegativeSentimentRate',
    descriptionKey: 'Dashboard::WidgetNegativeSentimentDesc',
    metricDescriptionKey: 'Dashboard::NegativeSentimentDescription',
    icon: 'fa-face-frown',
    layout: 'grouped',
    metricId: MetricIds.NegativeSentiment,
    previewValue: { value: 12, totalCount: 100 },
    endpoints: {
      value: `${API_BASE}/negative-sentiment-value`,
      trend: `${API_BASE}/negative-sentiment-trend`,
    },
  },
  {
    id: 'nps',
    component: 'nps',
    titleKey: 'Dashboard::NPS',
    descriptionKey: 'Dashboard::WidgetNpsDesc',
    metricDescriptionKey: 'Dashboard::NpsDescription',
    icon: 'fa-chart-line',
    layout: 'grouped',
    metricId: MetricIds.Nps,
    previewValue: { value: 1, totalCount: 1 },
    endpoints: {
      value: `${API_BASE}/nps-value`,
      trend: `${API_BASE}/nps-trend`,
    },
  },
];

const EXTRA_METRIC_ENDPOINTS: Partial<Record<MetricIds, MetricEndpoints>> = {
  [MetricIds.CsatPercentage]: {
    value: `${API_BASE}/csat-percentage-value`,
    trend: `${API_BASE}/csat-percentage-trend`,
  },
  [MetricIds.EstimatedCsatPercentage]: {
    value: `${API_BASE}/estimated-csat-percentage-value`,
    trend: `${API_BASE}/estimated-csat-percentage-trend`,
  },
  [MetricIds.ResolutionType]: {
    value: `${API_BASE}/resolution-type-value`,
    trend: `${API_BASE}/resolution-type-trend`,
  },
};

export const DEFS_BY_METRIC_ID = DashboardHelper.buildDefsByMetricId(DASHBOARD_WIDGET_DEFS);

export const DEFAULT_PREVIEW_TREND = DashboardHelper.DEFAULT_PREVIEW_TREND;

export const METRIC_ENDPOINTS: Record<MetricIds, MetricEndpoints> = DashboardHelper.buildMetricEndpoints(
  DASHBOARD_WIDGET_DEFS,
  EXTRA_METRIC_ENDPOINTS
);
